import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { fieldErrorMessage } from '../fieldTypes';
import { errorMessage } from '../errors';
import { SOURCE_LABELS } from '../sources';
import { Alert, Button, Pill, SearchField, Spinner, TextField } from '../ui';

const SEARCH_DEBOUNCE_MS = 400;
export const MIN_QUERY = 2;
const CACHE_SIZE = 20;

// The last answers, by search: typing back to an earlier query asks TMDB again only past 20.
const searchCache = new Map();
const remember = (key, results) => {
  searchCache.delete(key);
  searchCache.set(key, results);
  while (searchCache.size > CACHE_SIZE) searchCache.delete(searchCache.keys().next().value);
};
/** What is owned has changed (an item was added): searches are asked again. */
export const forgetSearches = () => searchCache.clear();

/**
 * The message for a refused search or add, by the API's code — naming the
 * source that failed when the answer says which (every source but TMDB,
 * whose answers stay those of before: its messages name it already).
 */
export const sourceError = (t, error, fields = []) => {
  const code = error?.response?.data?.code;
  const named = error?.response?.data?.source;
  if (code === 'client_outdated') return t('addFromSource.errors.client_outdated');
  const fieldMessage = fieldErrorMessage(t, error, fields);
  if (fieldMessage) return fieldMessage;
  if (named && ['source_busy', 'source_not_found', 'source_unavailable', 'source_error'].includes(code)) {
    return t(`addFromSource.errors.named.${code}`, { source: SOURCE_LABELS[named] || named });
  }
  if (['source_busy', 'source_not_found', 'source_unavailable', 'source_error', 'rate_limited'].includes(code)) {
    return t(`addFromSource.errors.${code}`);
  }
  if (code === 'conversion_running') return t('itemForm.conversionRunning');
  return t('addFromSource.errors.generic', { error: errorMessage(t, error) });
};

/**
 * TMDB search as you type (kind movie or tv): a title, an optional year,
 * results with their poster and "already in a collection". A pause of 400 ms
 * sends a search; an answer to an older search is dropped, and one of the
 * last 20 searches is not sent again. Used to add an item and to preview an
 * item type's integration.
 */
function TmdbSearch({ kind, lang, selected, onSelect, onManual, autoFocus = true, listStyle }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [year, setYear] = useState('');
  const [results, setResults] = useState(null);   // null: nothing searched yet
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const counter = useRef(0);
  const searchRef = useRef(null);

  // After the dialog around it has placed its own initial focus.
  useEffect(() => {
    if (!autoFocus) return undefined;
    const timer = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  useEffect(() => {
    const q = query.trim();
    const y = year.trim();
    if (q.length < MIN_QUERY || (y !== '' && !/^\d{4}$/.test(y))) {
      counter.current += 1;   // an answer still on its way is now stale
      setResults(null);
      setSearching(false);
      return undefined;
    }
    const key = [kind, lang, y, q.toLowerCase()].join('|');
    if (searchCache.has(key)) {
      counter.current += 1;
      setResults(searchCache.get(key));
      setSearching(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      const request = ++counter.current;
      setSearching(true);
      api.get('/sources/tmdb/search', { params: { kind, q, year: y || undefined, lang } })
        .then(response => {
          const found = response.data.results || [];
          remember(key, found);
          if (request !== counter.current) return;
          setResults(found);
          setError(null);
        })
        .catch(err => {
          if (request !== counter.current) return;
          setError(sourceError(t, err));
        })
        .finally(() => { if (request === counter.current) setSearching(false); });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, year, kind, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = (result) => `${result.title}${result.year ? ` (${result.year})` : ''}`;

  return (
    <div className="tmdb-search">
      <div className="tmdb-search__fields">
        <SearchField ref={searchRef} value={query} className="tmdb-search__query"
          onChange={(text) => { setQuery(text); onSelect(null); setError(null); }}
          placeholder={t(kind === 'tv' ? 'addFromSource.searchPlaceholderTv' : 'addFromSource.searchPlaceholderMovie')}
          label={t('addFromSource.searchLabel')} />
        <TextField inputMode="numeric" maxLength={4} value={year} className="tmdb-search__year"
          onChange={(e) => { setYear(e.target.value.replace(/\D/g, '')); onSelect(null); setError(null); }}
          placeholder={t('addFromSource.yearPlaceholder')} label={t('addFromSource.yearLabel')} hideLabel />
      </div>

      <p aria-live="polite" className="tmdb-search__status">
        {searching ? <><Spinner size={14} /> {t('addFromSource.searching')}</>
          : results && query.trim().length >= MIN_QUERY ? t('addFromSource.resultCount', { count: results.length }) : ''}
      </p>
      {error && <Alert tone="danger">{error}</Alert>}

      <ul aria-label={t('addFromSource.results')} className="tmdb-results" style={listStyle}>
        {(results || []).map(result => {
          const isSelected = selected?.tmdb_id === result.tmdb_id;
          return (
            <li key={result.tmdb_id}>
              <button type="button" className={`source-result${isSelected ? ' is-selected' : ''}`}
                aria-pressed={isSelected} onClick={() => onSelect(result)}>
                {result.poster_thumb
                  ? <img className="source-poster" src={result.poster_thumb} alt="" loading="lazy" />
                  : <span className="source-poster source-poster--empty" aria-hidden="true" />}
                <span className="source-result__text">
                  <span className="source-result__title">{title(result)}</span>
                  {result.original_title && result.original_title !== result.title && (
                    <span className="source-result__original">{result.original_title}</span>
                  )}
                  {result.overview && <span className="source-result__overview">{result.overview}</span>}
                  {result.owned.length > 0 && <Pill tone="amber" className="source-result__owned">{t('addFromSource.owned')}</Pill>}
                </span>
              </button>
            </li>
          );
        })}
        {results && results.length === 0 && !searching && query.trim().length >= MIN_QUERY && (
          <li className="tmdb-results__none">
            <span>{t('addFromSource.noResult')}</span>
            {onManual && <Button variant="secondary" size="sm" onClick={onManual}>{t('addFromSource.enterByHand')}</Button>}
          </li>
        )}
      </ul>
    </div>
  );
}

export default TmdbSearch;
